import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import TotalProjectsIcon from '../icons/TotalProjectsIcon';
import './RenameProjectDialog.css';

interface RenameProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onRename: (newName: string) => Promise<void>;
  projectName: string;
  isCreate?: boolean,
  dialogTitle?: string
}

const RenameProjectDialog: React.FC<RenameProjectDialogProps> = ({
  isOpen,
  onClose,
  onRename,
  projectName,
  dialogTitle = 'Rename Project',
  isCreate = false

}) => {
  const [newName, setNewName] = useState(projectName || '');
  const [isRenaming, setIsRenaming] = useState(false);

  useEffect(() => {
    if (projectName) {
      setNewName(projectName);
    }
  }, [projectName]);

  const handleRename = async () => {
    if (newName.trim() && newName.trim() !== projectName) {
      setIsRenaming(true);
      try {
        await onRename(newName.trim());
        onCloseDialog();
      } catch (error) {
        // Error handling is done in the parent component
      } finally {
        setIsRenaming(false);
      }
    }
  };

  const onCloseDialog = () => {
    setNewName('');
    onClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    // Limit to 55 characters
    setNewName(value.substring(0, 55));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isRenaming && newName.trim() && newName.trim() !== projectName) {
      e.preventDefault();
      handleRename();
    }
  };

  const isRenameDisabled = !newName.trim() || newName.trim() === projectName || isRenaming;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCloseDialog}
      title=" "
      maxWidth="359px"
      closeOnOverlayClick={false}
      className="rename-dialog-modal"
    >
      <div className="rename-dialog-content">
        <div className="rename-dialog-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120" fill="none">
            <path d="M60 5.15527C90.2901 5.15527 114.845 29.7099 114.845 60C114.845 90.2901 90.2901 114.845 60 114.845C29.7099 114.845 5.15527 90.2901 5.15527 60C5.15527 29.7099 29.7099 5.15527 60 5.15527ZM60 14.8447C35.0616 14.8447 14.8447 35.0616 14.8447 60C14.8447 84.9384 35.0616 105.155 60 105.155C84.9384 105.155 105.155 84.9384 105.155 60C105.155 35.0616 84.9384 14.8447 60 14.8447ZM64.8447 55.1553H80V64.8447H64.8447V80H55.1553V64.8447H40V55.1553H55.1553V40H64.8447V55.1553Z" fill="url(#paint0_linear_18_9926)" />
            <defs>
              <linearGradient id="paint0_linear_18_9926" x1="5.15527" y1="60" x2="114.845" y2="60" gradientUnits="userSpaceOnUse">
                <stop stopColor="#00D650" />
                <stop offset="1" stopColor="#0081FE" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <h2 className="rename-dialog-title">{dialogTitle}</h2>

        <div className="rename-dialog-input-container">
          <textarea
            value={newName}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Add your project name here..."
            autoFocus
            className="rename-dialog-input"
            rows={2}
          />
        </div>

        <button
          onClick={handleRename}
          disabled={isRenameDisabled}
          className="rename-dialog-button"
        >
          {isCreate ? 'Create ' : (isRenaming ? 'Renaming...' : 'Rename')}
        </button>
        <div className="max-characters">Maximum 50 characters allowed.</div>
      </div>
    </Modal>
  );
};

export default RenameProjectDialog;